import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFinancementList, addFinancement, updateFinancement, deleteFinancement } from '../../../api/redux/slices/Admin';
import { imageApi } from '../../../api/redux/slices/Public';
import { LoaderComponent } from '../../../helpers/components/loader';
import { convertDate, roundToThreeDecimals } from '../../../helpers/modules';
import { icons } from '../../../assets/icons';
import { toast } from 'react-toastify';
import './index.scss';

const FinancementList = () => {
    const dispatch = useDispatch();
    const { financements, loading, total } = useSelector((state) => state.admin);
    const token = localStorage.getItem('accesstoken');
    
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedFinancement, setSelectedFinancement] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [solde, setSolde] = useState(null);
    const [fileUploaded, setFileUploaded] = useState(null);
    const [editFileUploaded, setEditFileUploaded] = useState(null);
    const [formData, setFormData] = useState({
        sujet: '',
        description: '',
        price: '',
        date_limite: '',
        devise_proof: null
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const limit = 9;

    const fetchFinancements = useCallback(async () => {
        try {
            const result = await dispatch(getFinancementList({ 
                token, 
                page,
                status: statusFilter || undefined
            })).unwrap();
            if (result?.data?.solde) {
                setSolde(result.data.solde);
            }
        } catch (error) {
            console.error('Error fetching financements:', error);
        }
    }, [dispatch, token, page, statusFilter]);

    useEffect(() => {
        fetchFinancements();
    }, [fetchFinancements]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        setPage(1);
    };

    const handleShowDetails = (financement) => {
        setSelectedFinancement(financement);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedFinancement(null);
    };

    const handleOpenAddModal = () => {
        setFormData({ sujet: '', description: '', price: '', date_limite: '', devise_proof: null });
        setFileUploaded(null);
        setFormErrors({});
        setShowAddModal(true);
    };

    const handleCloseAddModal = () => {
        setShowAddModal(false);
        setFormData({ sujet: '', description: '', price: '', date_limite: '', devise_proof: null });
        setFileUploaded(null);
        setFormErrors({});
    };

    const handleOpenEditModal = (financement) => {
        setSelectedFinancement(financement);
        // Format date_limite to YYYY-MM-DD for the input
        let formattedDate = '';
        if (financement.date_limite) {
            const d = new Date(financement.date_limite);
            if (!isNaN(d.getTime())) {
                formattedDate = d.toISOString().split('T')[0];
            }
        }
        setFormData({
            sujet: financement.sujet || '',
            description: financement.description || '',
            price: financement.price || '',
            date_limite: formattedDate,
            devise_proof: financement.devise_proof || null
        });
        setEditFileUploaded(null);
        setFormErrors({});
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedFinancement(null);
        setFormData({ sujet: '', description: '', price: '', date_limite: '', devise_proof: null });
        setEditFileUploaded(null);
        setFormErrors({});
    };

    const handleOpenDeleteModal = (financement) => {
        setSelectedFinancement(financement);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedFinancement(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFileUploaded({ file, preview: URL.createObjectURL(file) });
        }
    };

    const handleEditFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEditFileUploaded({ file, preview: URL.createObjectURL(file) });
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.sujet.trim()) {
            errors.sujet = 'Le sujet est requis';
        }
        if (!formData.description.trim()) {
            errors.description = 'La description est requise';
        }
        if (!formData.price || parseFloat(formData.price) <= 0) {
            errors.price = 'Le montant doit être supérieur à 0';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const pushWithImage = (fileUploaded, financementData) => {
        let formDataUpload = new FormData();
        formDataUpload.append('image', fileUploaded.file);

        dispatch(imageApi({ token: token, formDta: formDataUpload }))
            .unwrap()
            .then((res) => {
                submitFinancement({
                    ...financementData,
                    devise_proof: {
                        public_id: res.public_id,
                        secure_url: res.secure_url
                    }
                });
            })
            .catch((err) => {
                toast.error(err?.message || 'Erreur lors de l\'upload du fichier');
                setSubmitting(false);
            });
    };

    const submitFinancement = async (data) => {
        try {
            await dispatch(addFinancement({
                token,
                data: data
            })).unwrap();
            
            toast.success('Demande de financement créée avec succès');
            handleCloseAddModal();
            setPage(1);
            setStatusFilter('');
            fetchFinancements();
        } catch (error) {
            toast.error(error?.message || 'Erreur lors de la création');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setSubmitting(true);
        
        const financementData = {
            sujet: formData.sujet,
            description: formData.description,
            price: parseFloat(formData.price)
        };

        // Add date_limite if provided
        if (formData.date_limite) {
            financementData.date_limite = formData.date_limite;
        }

        if (fileUploaded) {
            pushWithImage(fileUploaded, financementData);
        } else {
            submitFinancement(financementData);
        }
    };

    const submitUpdate = async (data) => {
        try {
            await dispatch(updateFinancement({
                token,
                id: selectedFinancement._id,
                data: data
            })).unwrap();
            
            toast.success('Demande de financement modifiée avec succès');
            handleCloseEditModal();
            fetchFinancements();
        } catch (error) {
            toast.error(error?.message || 'Erreur lors de la modification');
        } finally {
            setSubmitting(false);
        }
    };

    const pushUpdateWithImage = (fileUploaded, updateData) => {
        let formDataUpload = new FormData();
        formDataUpload.append('image', fileUploaded.file);

        dispatch(imageApi({ token: token, formDta: formDataUpload }))
            .unwrap()
            .then((res) => {
                submitUpdate({
                    ...updateData,
                    devise_proof: {
                        public_id: res.public_id,
                        secure_url: res.secure_url
                    }
                });
            })
            .catch((err) => {
                toast.error(err?.message || 'Erreur lors de l\'upload du fichier');
                setSubmitting(false);
            });
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setSubmitting(true);
        
        const updateData = {
            sujet: formData.sujet,
            description: formData.description,
            price: parseFloat(formData.price),
            date_limite: formData.date_limite || null
        };

        if (editFileUploaded) {
            pushUpdateWithImage(editFileUploaded, updateData);
        } else {
            submitUpdate(updateData);
        }
    };

    const handleDelete = async () => {
        setSubmitting(true);
        
        try {
            await dispatch(deleteFinancement({
                token,
                id: selectedFinancement._id
            })).unwrap();
            
            toast.success('Demande de financement supprimée avec succès');
            handleCloseDeleteModal();
            fetchFinancements();
            
        } catch (error) {
            toast.error(error?.message || 'Erreur lors de la suppression');
        } finally {
            setSubmitting(false);
        }
    };

    const truncateText = (text, maxLength) => {
        if (!text) return '-';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const getStatusBadge = (status) => {
        switch(status) {
            case 'accepted':
                return { className: 'status-accepted', label: 'Accepté', icon: 'BsCheckCircle' };
            case 'refused':
                return { className: 'status-refused', label: 'Refusé', icon: 'BsXCircle' };
            default:
                return { className: 'status-pending', label: 'En cours', icon: 'BsClock' };
        }
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div id="financementList">
            <div className="financement-header">
                <div className="header-content">
                    <div className="header-icon">
                        <icons.BsCashStack style={{ fontSize: '24px' }} />
                    </div>
                    <div className="header-text">
                        <h1>Demandes de Financement</h1>
                        <p>Gérez vos demandes de financement et suivez leur statut</p>
                    </div>
                </div>
                <div className="header-actions">
                    <button className="btn-add" onClick={handleOpenAddModal}>
                        <icons.BsPlus style={{ fontSize: '20px' }} />
                        <span>Nouvelle demande</span>
                    </button>
                </div>
            </div>

            {solde && (
                <div className="solde-section">
                    <div className="solde-card">
                        <div className="solde-item parrainage">
                            <div className="solde-icon">
                                <icons.BsGift style={{ fontSize: '20px' }} />
                            </div>
                            <div className="solde-info">
                                <span className="solde-label">Total Parrainage Reçu</span>
                                <span className="solde-value positive">
                                    + {roundToThreeDecimals(solde.totalParrainageReceived)} DT
                                </span>
                            </div>
                        </div>
                        <div className="solde-divider"></div>
                        <div className="solde-item financement">
                            <div className="solde-icon">
                                <icons.BsCashStack style={{ fontSize: '20px' }} />
                            </div>
                            <div className="solde-info">
                                <span className="solde-label">Total Financements Acceptés</span>
                                <span className="solde-value negative">
                                    - {roundToThreeDecimals(solde.totalAcceptedFinancements)} DT
                                </span>
                            </div>
                        </div>
                        <div className="solde-divider"></div>
                        <div className={`solde-item total ${solde.solde >= 0 ? 'positive-bg' : 'negative-bg'}`}>
                            <div className="solde-icon">
                                <icons.BsWallet style={{ fontSize: '20px' }} />
                            </div>
                            <div className="solde-info">
                                <span className="solde-label">Solde Disponible</span>
                                <span className={`solde-value ${solde.solde >= 0 ? 'positive' : 'negative'}`}>
                                    {solde.solde >= 0 ? '+ ' : '- '}
                                    {roundToThreeDecimals(Math.abs(solde.solde))} DT
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="stats-filter-row">
                <div className="header-stats">
                    <div className="stat-card">
                        <span className="stat-label">Total Demandes</span>
                        <span className="stat-value">{total}</span>
                    </div>
                    <div className="stat-card highlight">
                        <span className="stat-label">En cours</span>
                        <span className="stat-value">
                            {financements?.filter(f => f.status === 'en cours').length || 0}
                        </span>
                    </div>
                </div>
                <div className="status-filter">
                    <button className={`filter-btn ${statusFilter === '' ? 'active' : ''}`} onClick={() => handleStatusFilter('')}>Tous</button>
                    <button className={`filter-btn pending ${statusFilter === 'en cours' ? 'active' : ''}`} onClick={() => handleStatusFilter('en cours')}>
                        <icons.BsClock style={{ fontSize: '14px' }} />En cours
                    </button>
                    <button className={`filter-btn accepted ${statusFilter === 'accepted' ? 'active' : ''}`} onClick={() => handleStatusFilter('accepted')}>
                        <icons.BsCheckCircle style={{ fontSize: '14px' }} />Accepté
                    </button>
                    <button className={`filter-btn refused ${statusFilter === 'refused' ? 'active' : ''}`} onClick={() => handleStatusFilter('refused')}>
                        <icons.BsXCircle style={{ fontSize: '14px' }} />Refusé
                    </button>
                </div>
            </div>

            {loading ? (
                <LoaderComponent />
            ) : (
                <>
                    <div className="financement-grid">
                        {financements?.map((financement, index) => {
                            const statusBadge = getStatusBadge(financement.status);
                            const StatusIcon = icons[statusBadge.icon];
                            const isPending = financement.status === 'en cours';
                            
                            return (
                                <div className="financement-card" key={financement._id || index}>
                                    <div className={`card-status-bar ${statusBadge.className}`}></div>
                                    <div className="card-content">
                                        <div className="card-header">
                                            <div className="sujet">
                                                <icons.BsFileText className="sujet-icon" />
                                                <span>{truncateText(financement.sujet, 70)}</span>
                                            </div>
                                            <div className={`status-badge ${statusBadge.className}`}>
                                                <StatusIcon style={{ fontSize: '14px' }} />
                                                <span>{statusBadge.label}</span>
                                            </div>
                                        </div>
                                        <div className="card-body">
                                            <p className="description">{truncateText(financement.description, 50)}</p>
                                            {financement.date_limite && (
                                                <div className="date-limite-row">
                                                    <icons.BsCalendar style={{ fontSize: '12px' }} />
                                                    <span>Date limite : {convertDate(financement.date_limite, 'DD/MM/YYYY')}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="card-footer">
                                            <div className="price-section">
                                                <span className="price-label">Montant</span>
                                                <span className="price-value">{roundToThreeDecimals(financement.price)} DT</span>
                                            </div>
                                            <div className="actions-section">
                                                <span className="date">{convertDate(financement.createdAt, 'DD/MM/YYYY')}</span>
                                                {isPending && (
                                                    <div className="action-btns">
                                                        <button className="btn-icon edit" onClick={() => handleOpenEditModal(financement)} title="Modifier">
                                                            <icons.BsPencil style={{ fontSize: '14px' }} />
                                                        </button>
                                                        <button className="btn-icon delete" onClick={() => handleOpenDeleteModal(financement)} title="Supprimer">
                                                            <icons.BsTrash style={{ fontSize: '14px' }} />
                                                        </button>
                                                    </div>
                                                )}
                                                <button className="btn-details" onClick={() => handleShowDetails(financement)} title="Voir détails">
                                                    <icons.BsEye style={{ fontSize: '16px' }} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {financements?.length === 0 && !loading && (
                        <div className="empty-state">
                            <icons.BsInbox style={{ fontSize: '48px', color: '#94a3b8' }} />
                            <h3>Aucune demande de financement</h3>
                            <p>Vous n'avez pas encore soumis de demande de financement.</p>
                            <button className="btn-add-empty" onClick={handleOpenAddModal}>
                                <icons.BsPlus style={{ fontSize: '18px' }} />
                                <span>Créer une demande</span>
                            </button>
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className="pagination">
                            <button className="page-btn" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
                                <icons.BsChevronLeft />
                            </button>
                            {[...Array(totalPages)].map((_, index) => (
                                <button key={index} className={`page-btn ${page === index + 1 ? 'active' : ''}`} onClick={() => handlePageChange(index + 1)}>
                                    {index + 1}
                                </button>
                            ))}
                            <button className="page-btn" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>
                                <icons.BsChevronRight />
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Details Modal */}
            {showModal && selectedFinancement && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Détails de la demande</h2>
                            <button className="modal-close" onClick={handleCloseModal}><icons.BsX style={{ fontSize: '24px' }} /></button>
                        </div>
                        <div className="modal-body">
                            <div className={`modal-status-bar ${getStatusBadge(selectedFinancement.status).className}`}>
                                {(() => {
                                    const badge = getStatusBadge(selectedFinancement.status);
                                    const StatusIcon = icons[badge.icon];
                                    return <><StatusIcon style={{ fontSize: '20px' }} /><span>{badge.label}</span></>;
                                })()}
                            </div>
                            <div className="detail-row"><label>Sujet</label><p>{selectedFinancement.sujet || '-'}</p></div>
                            <div className="detail-row"><label>Description</label><p className="description-full">{selectedFinancement.description || '-'}</p></div>
                            <div className="detail-row"><label>Montant demandé</label><p className="price-highlight">{roundToThreeDecimals(selectedFinancement.price)} DT</p></div>
                            {selectedFinancement.date_limite && (
                                <div className="detail-row">
                                    <label>Date limite</label>
                                    <p className="date-limite-highlight">
                                        <icons.BsCalendar style={{ marginRight: '8px' }} />
                                        {convertDate(selectedFinancement.date_limite, 'DD/MM/YYYY')}
                                    </p>
                                </div>
                            )}
                            <div className="detail-row"><label>Date de demande</label><p>{convertDate(selectedFinancement.createdAt, 'DD/MM/YYYY à HH:mm')}</p></div>
                            
                            {selectedFinancement.devise_proof?.secure_url && (
                                <div className="detail-row">
                                    <label>Justificatif (Devis)</label>
                                    <div className="devise-proof-preview">
                                        <img 
                                            src={selectedFinancement.devise_proof.secure_url} 
                                            alt="Justificatif devis" 
                                            className="devise-proof-img"
                                            onClick={() => window.open(selectedFinancement.devise_proof.secure_url, '_blank')}
                                        />
                                        <a 
                                            href={selectedFinancement.devise_proof.secure_url} 
                                            target="_blank" 
                                            rel="noreferrer"
                                            className="devise-proof-link"
                                        >
                                            <icons.BsEye style={{ fontSize: '16px' }} />
                                            Voir le justificatif
                                        </a>
                                    </div>
                                </div>
                            )}

                            {selectedFinancement.status !== 'en cours' && selectedFinancement.message && (
                                <div className={`admin-message ${selectedFinancement.status === 'accepted' ? 'message-accepted' : 'message-refused'}`}>
                                    <div className="message-header">
                                        {selectedFinancement.status === 'accepted' ? <icons.BsCheckCircle style={{ fontSize: '18px' }} /> : <icons.BsXCircle style={{ fontSize: '18px' }} />}
                                        <span>{selectedFinancement.status === 'accepted' ? 'Note d\'acceptation' : 'Note de refus'}</span>
                                    </div>
                                    <p className="message-text">{selectedFinancement.message}</p>
                                </div>
                            )}
                            {selectedFinancement.status === 'en cours' && (
                                <div className="pending-message"><icons.BsClock style={{ fontSize: '18px' }} /><span>Votre demande est en cours de traitement</span></div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Add Modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={handleCloseAddModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Nouvelle demande de financement</h2>
                            <button className="modal-close" onClick={handleCloseAddModal}><icons.BsX style={{ fontSize: '24px' }} /></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label htmlFor="sujet">Sujet <span className="required">*</span></label>
                                    <input type="text" id="sujet" name="sujet" value={formData.sujet} onChange={handleInputChange} placeholder="Entrez le sujet de votre demande" className={`form-input ${formErrors.sujet ? 'input-error' : ''}`} />
                                    {formErrors.sujet && <span className="error-text">{formErrors.sujet}</span>}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="description">Description <span className="required">*</span></label>
                                    <textarea id="description" name="description" value={formData.description} onChange={handleInputChange} placeholder="Décrivez votre demande de financement" rows="4" className={`form-input form-textarea ${formErrors.description ? 'input-error' : ''}`} />
                                    {formErrors.description && <span className="error-text">{formErrors.description}</span>}
                                </div>
                                    <div className="form-group">
                                        <label htmlFor="price">Montant (DT) <span className="required">*</span></label>
                                        <input type="number" id="price" name="price" value={formData.price} onChange={handleInputChange} placeholder="0.000" step="0.001" min="0" className={`form-input ${formErrors.price ? 'input-error' : ''}`} />
                                        {formErrors.price && <span className="error-text">{formErrors.price}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="date_limite">Date limite</label>
                                        <input type="date" id="date_limite" name="date_limite" value={formData.date_limite} onChange={handleInputChange} className="form-input" />
                                    </div>
                                
                                <div className="form-group">
                                    <label htmlFor="devise_proof">Justificatif (Devis - Image ou PDF)</label>
                                    <div className="file-upload-container">
                                        <input type="file" id="devise_proof" accept=".jpg,.jpeg,.png,.pdf" onChange={handleFileChange} className="file-input-hidden" />
                                        <label htmlFor="devise_proof" className="file-upload-label">
                                            <icons.BsUpload style={{ fontSize: '18px' }} />
                                            <span>{fileUploaded ? fileUploaded.file.name : 'Choisir un fichier'}</span>
                                        </label>
                                    </div>
                                    {fileUploaded && (
                                        <div className="file-preview">
                                            {fileUploaded.file.type.startsWith('image/') ? (
                                                <img src={fileUploaded.preview} alt="Preview" className="file-preview-img" />
                                            ) : (
                                                <div className="file-preview-pdf">
                                                    <span style={{ fontSize: '24px' }}>📄</span>
                                                    <span>{fileUploaded.file.name}</span>
                                                </div>
                                            )}
                                            <button type="button" className="file-remove" onClick={() => setFileUploaded(null)}>
                                                <icons.BsX style={{ fontSize: '16px' }} />
                                            </button>
                                        </div>
                                    )}
                                    <span className="file-hint">Formats acceptés: JPG, PNG, PDF</span>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={handleCloseAddModal} disabled={submitting}>Annuler</button>
                                <button type="submit" className="btn-submit" disabled={submitting}>
                                    {submitting ? <><span className="spinner"></span><span>Création...</span></> : <><icons.BsPlus style={{ fontSize: '18px' }} /><span>Créer la demande</span></>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedFinancement && (
                <div className="modal-overlay" onClick={handleCloseEditModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Modifier la demande</h2>
                            <button className="modal-close" onClick={handleCloseEditModal}><icons.BsX style={{ fontSize: '24px' }} /></button>
                        </div>
                        <form onSubmit={handleUpdate}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label htmlFor="edit-sujet">Sujet <span className="required">*</span></label>
                                    <input type="text" id="edit-sujet" name="sujet" value={formData.sujet} onChange={handleInputChange} placeholder="Entrez le sujet" className={`form-input ${formErrors.sujet ? 'input-error' : ''}`} />
                                    {formErrors.sujet && <span className="error-text">{formErrors.sujet}</span>}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="edit-description">Description <span className="required">*</span></label>
                                    <textarea id="edit-description" name="description" value={formData.description} onChange={handleInputChange} placeholder="Décrivez votre demande" rows="4" className={`form-input form-textarea ${formErrors.description ? 'input-error' : ''}`} />
                                    {formErrors.description && <span className="error-text">{formErrors.description}</span>}
                                </div>
                                    <div className="form-group">
                                        <label htmlFor="edit-price">Montant (DT) <span className="required">*</span></label>
                                        <input type="number" id="edit-price" name="price" value={formData.price} onChange={handleInputChange} placeholder="0.000" step="0.001" min="0" className={`form-input ${formErrors.price ? 'input-error' : ''}`} />
                                        {formErrors.price && <span className="error-text">{formErrors.price}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="edit-date_limite">Date limite</label>
                                        <input type="date" id="edit-date_limite" name="date_limite" value={formData.date_limite} onChange={handleInputChange} className="form-input" />
                                    </div>
                                
                                {formData.devise_proof?.secure_url && !editFileUploaded && (
                                    <div className="form-group">
                                        <label>Justificatif actuel</label>
                                        <div className="current-file-preview">
                                            <img src={formData.devise_proof.secure_url} alt="Justificatif actuel" className="current-file-img" />
                                            <span className="current-file-label">Fichier existant</span>
                                        </div>
                                    </div>
                                )}
                                
                                <div className="form-group">
                                    <label htmlFor="edit-devise_proof">
                                        {formData.devise_proof?.secure_url ? 'Remplacer le justificatif' : 'Justificatif (Devis - Image ou PDF)'}
                                    </label>
                                    <div className="file-upload-container">
                                        <input type="file" id="edit-devise_proof" accept=".jpg,.jpeg,.png,.pdf" onChange={handleEditFileChange} className="file-input-hidden" />
                                        <label htmlFor="edit-devise_proof" className="file-upload-label">
                                            <icons.BsUpload style={{ fontSize: '18px' }} />
                                            <span>{editFileUploaded ? editFileUploaded.file.name : 'Choisir un nouveau fichier'}</span>
                                        </label>
                                    </div>
                                    {editFileUploaded && (
                                        <div className="file-preview">
                                            {editFileUploaded.file.type.startsWith('image/') ? (
                                                <img src={editFileUploaded.preview} alt="Preview" className="file-preview-img" />
                                            ) : (
                                                <div className="file-preview-pdf">
                                                    <span style={{ fontSize: '24px' }}>📄</span>
                                                    <span>{editFileUploaded.file.name}</span>
                                                </div>
                                            )}
                                            <button type="button" className="file-remove" onClick={() => setEditFileUploaded(null)}>
                                                <icons.BsX style={{ fontSize: '16px' }} />
                                            </button>
                                        </div>
                                    )}
                                    <span className="file-hint">Formats acceptés: JPG, PNG, PDF</span>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={handleCloseEditModal} disabled={submitting}>Annuler</button>
                                <button type="submit" className="btn-submit" disabled={submitting}>
                                    {submitting ? <><span className="spinner"></span><span>Modification...</span></> : <><icons.BsPencil style={{ fontSize: '18px' }} /><span>Modifier</span></>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedFinancement && (
                <div className="modal-overlay" onClick={handleCloseDeleteModal}>
                    <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Confirmer la suppression</h2>
                            <button className="modal-close" onClick={handleCloseDeleteModal}><icons.BsX style={{ fontSize: '24px' }} /></button>
                        </div>
                        <div className="modal-body">
                            <div className="confirm-content">
                                <div className="confirm-icon">
                                    <icons.BsExclamationTriangle style={{ fontSize: '48px', color: '#ef4444' }} />
                                </div>
                                <p className="confirm-text">
                                    Êtes-vous sûr de vouloir supprimer cette demande de financement ?
                                </p>
                                <div className="confirm-details">
                                    <span className="confirm-sujet">{selectedFinancement.sujet}</span>
                                    <span className="confirm-price">{roundToThreeDecimals(selectedFinancement.price)} DT</span>
                                </div>
                                <p className="confirm-warning">Cette action est irréversible.</p>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn-cancel" onClick={handleCloseDeleteModal} disabled={submitting}>Annuler</button>
                            <button type="button" className="btn-delete" onClick={handleDelete} disabled={submitting}>
                                {submitting ? <><span className="spinner"></span><span>Suppression...</span></> : <><icons.BsTrash style={{ fontSize: '18px' }} /><span>Supprimer</span></>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinancementList;